/* stylelint-disable */

import React, { ReactNode, useState, useRef, useLayoutEffect } from 'react'
import styled, { css } from 'styled-components'
import { colors, ThemeColor } from 'utils/colors'

import Text from '../Text'
import Tooltip from '../Tooltip'
import { isLegacy, LeadDetailsDesignVersion } from '../utils/designVersionHelpers'

type Props = {
  bgColor?: string
  children?: ReactNode
  isActive?: boolean
  isReadOnly?: boolean
  onClick?: () => void
  position?: 'last' | 'normal' | 'first'
  textColor?: ThemeColor
  designVersion?: LeadDetailsDesignVersion
}

const TimeLineBadge: React.FC<Props> = (props) => {
  const {
    children,
    textColor = 'white',
    bgColor = colors.gray4,
    isReadOnly,
    onClick,
    position = 'last',
    isActive,
    designVersion = 'v1',
  } = props
  const [showTooltip, setShowTooltip] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const textContent = () => (
    <TextWrapper ref={ref} textColor={textColor}>
      <Text color={textColor}>{children}</Text>
    </TextWrapper>
  )

  const content = () => {
    if (typeof children !== 'string') {
      return children
    }

    if (showTooltip) {
      return <Tooltip title={children}>{textContent()}</Tooltip>
    }

    return textContent()
  }

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const checkTruncation = () => {
      const currentRef = ref.current
      if (currentRef) {
        const isTruncated = currentRef.scrollWidth > currentRef.offsetWidth + 1
        setShowTooltip(isTruncated)
      }
    }

    const measure = () => checkTruncation()

    const observer = new ResizeObserver(measure)
    observer.observe(el)

    requestAnimationFrame(measure)

    return () => observer.disconnect()
  }, [children])

  return (
    <Container
      bgColor={bgColor}
      className={position}
      isActive={isActive as boolean}
      isReadOnly={isReadOnly}
      onClick={onClick}
      textColor={textColor}
      isLegacy={isLegacy(designVersion)}
    >
      {content()}
    </Container>
  )
}

export default TimeLineBadge

const DeactivatedTimelineStyles = css`
  & div:last-of-type {
    color: ${(p) => p.theme.colors.gray5};
  }

  &.first,
  &.normal,
  &.last {
    background-color: ${(p) => p.theme.colors.gray2};
    border: 1px solid ${(p) => p.theme.colors.gray4};

    &:not(.first) {
      &::before {
        content: '';
        border-top: 1px solid ${(p) => p.theme.colors.gray4};
        border-right: 1px solid ${(p) => p.theme.colors.gray4};
        width: 21px;
        height: 21px;
        position: absolute;
        left: -10px;
        top: 4px;
        background-color: ${(p) => p.theme.colors.white};
        z-index: 1;
        transform: rotate(45deg);
        overflow: hidden;
      }

      margin-left: 2px;
      border-left: none;
    }

    &::after {
      right: -12px;
      top: 4px;
      background-color: ${(p) => p.theme.colors.gray2};
      border-top: 1px solid ${(p) => p.theme.colors.gray4};
      border-right: 1px solid ${(p) => p.theme.colors.gray4};
      width: 21px;
      height: 21px;
      z-index: 2;
    }
  }
`

const Container = styled.div<{
  bgColor: string
  isActive: boolean
  isReadOnly?: boolean
  textColor: string
  isLegacy: boolean
}>`
  cursor: ${(p) => (p.isReadOnly ? 'default' : p.onClick ? 'pointer' : 'default')};
  display: inline-block;
  position: relative;
  height: 32px;
  line-height: 32px;
  padding: 0 20px;
  border: 1px solid ${(p) => p.bgColor};
  text-align: center;
  background-color: ${(p) => p.bgColor};
  user-select: none;
  box-sizing: border-box;
  width: 300px;

  &.first {
    min-width: 90px;
    max-width: ${({ isLegacy }) => (isLegacy ? 'auto' : '300px')};
  }

  &.normal {
    min-width: ${({ isLegacy }) => (isLegacy ? '19px' : '90px')};
    max-width: ${({ isLegacy }) => (isLegacy ? 'auto' : '300px')};
    border-radius: 0;
  }

  &.first,
  &.normal {
    border-top-left-radius: 5px;
    border-bottom-left-radius: 5px;

    ::after {
      content: '';
      border-top: 2px solid white;
      border-right: 2px solid white;
      width: 23px;
      height: 20px;
      position: absolute;
      right: -13px;
      top: 4px;
      background-color: ${(p) => p.bgColor};
      z-index: 1;
      transform: rotate(45deg);
      overflow: hidden;
    }
  }

  &.last {
    min-width: 90px;
    max-width: ${({ isLegacy }) => (isLegacy ? 'auto' : '300px')};
    border-top-right-radius: 5px;
    border-bottom-right-radius: 5px;
  }

  > div {
    color: ${(p) => p.textColor};
  }

  ${(p) => !p.isActive && DeactivatedTimelineStyles}
`

const TextWrapper = styled.div<{ textColor: string }>`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 15px;

  > div {
    display: inline;
  }
`
